import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RulesModal.css';

interface RulesModalProps {
  open: boolean;
  onClose: () => void;
}

const RulesModal: React.FC<RulesModalProps> = ({ open, onClose }) => {
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
  <AnimatePresence>
    {open && (
      <motion.div
        className="rules-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      >
        <motion.div
          className="rules-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="rules-header">
            <h2 id={titleId}>How to Play</h2>
            <button className="rules-close" onClick={onClose} aria-label="Close rules">✕</button>
          </div>

          <div className="rules-body">
            <section>
              <h3>Objective</h3>
              <p>
                ACE is a card reduction game. Get rid of all your cards to become <strong>Safe</strong>.
                The last player holding cards is the <strong>Loser</strong>.
              </p>
            </section>

            <section>
              <h3>Setup</h3>
              <ul>
                <li>3–8 players, all 52 cards dealt</li>
                <li>Card ranking: <span className="rules-rank">2 &lt; 3 &lt; 4 &lt; 5 &lt; 6 &lt; 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A</span></li>
                <li>The player with the <strong>Ace of Spades</strong> plays first</li>
              </ul>
            </section>

            <section>
              <h3>
                <span className="rules-icon">♠</span>
                Following Suit
              </h3>
              <p>The first card played sets the <strong>lead suit</strong>. Every other player must play a card of that suit if they have one.</p>
              <p>If everyone follows suit, the <strong>highest card</strong> of the lead suit wins the trick. All cards are discarded and the winner leads the next round.</p>
            </section>

            <section>
              <h3>
                <span className="rules-icon rules-icon-red">✂</span>
                The Cut
              </h3>
              <p>If you have <strong>no cards</strong> of the lead suit, you may play any card. This is a <strong>"Cut"</strong> — the trick ends immediately.</p>
              <p>The player who played the <strong>highest lead-suit card</strong> must pick up all the cards on the table.</p>
              <p>That player then starts the next round.</p>
            </section>

            <section>
              <h3>
                <span className="rules-icon">🤝</span>
                Borrowing Cards
              </h3>
              <p>Between rounds, you can request to <strong>take all cards</strong> from a neighboring player.</p>
              <ul>
                <li>Only from <strong>adjacent</strong> active players</li>
                <li>It's all or nothing — no partial transfers</li>
                <li>The neighbor can <strong>accept or reject</strong></li>
                <li>If accepted, the neighbor becomes Safe instantly</li>
              </ul>
            </section>

            <section>
              <h3>
                <span className="rules-icon">⏱</span>
                Turn Timer
              </h3>
              <p>Each player has <strong>30 seconds</strong> to play. If time runs out, a card is auto-played for you.</p>
            </section>

            <section>
              <h3>Winning &amp; Losing</h3>
              <p>Empty your hand to become <strong>Safe</strong>. The game continues until one player remains — they lose!</p>
            </section>

            <section>
              <h3>
                <span className="rules-icon rules-icon-red">🂡</span>
                Loser Penalty (Rematch)
              </h3>
              <p>When you play again after a loss, the loser receives <strong>penalty cards</strong> before dealing:</p>
              <ul>
                <li><strong>1st loss:</strong> Loser receives A♠ and starts the game</li>
                <li><strong>2nd consecutive loss:</strong> A♠ + 1 more Ace</li>
                <li><strong>3rd consecutive loss:</strong> A♠ + 2 more Aces</li>
                <li>Beyond 4 Aces, penalty extends to <strong>Kings</strong></li>
              </ul>
              <p>Winning (becoming Safe) <strong>reduces your penalty by 1</strong>. Multiple players can carry penalties simultaneously.</p>
              <p>All players are notified of penalty cards at the start of each game.</p>
            </section>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};

export default RulesModal;
